<template>
  <div v-if="shouldRenderBadges" :class="$style.badgeContainer">
    <div :class="$style.badge" data-testid="main-badge">
      <div :class="$style.iconContainerSmall">
        <img
          :src="getAssociationLogoUrl(mainBadge.associationId)"
          :class="$style.customIcon"
          alt="Industry Association Badge"
        />
      </div>
      <strong>{{ mainBadge.name }}</strong>
      <HgTooltip placement="top" theme="secondary">
        <div :class="$style.badgeTooltip">
          <div :class="$style.iconContainer">
            <img
              :src="getAssociationLogoUrl(mainBadge.associationId)"
              :class="$style.customIcon"
              alt="Industry Association Badge"
            />
          </div>
          <strong>
            <span>{{ mainBadge.name }}</span>
          </strong>
          <div>
            {{
              t(
                `industryAssociations.tooltipDescription.${
                  isUspiAssociation(mainBadge.associationId) ? 'uspi' : 'svit'
                }`,
              )
            }}
          </div>
        </div>
      </HgTooltip>
    </div>
    <div
      v-if="displaySecondBadge"
      :class="$style.secondaryBadge"
      data-testid="secondary-badge"
      @click="toggleAssociationsModal"
    >
      <strong>+ {{ badges.associations.length - 1 }} {{ t('industryAssociations.more') }}</strong>
    </div>
  </div>
  <HgModal
    v-if="isAssociationsModalOpen && shouldRenderBadges"
    v-model="isAssociationsModalOpen"
    data-testid="associations-modal"
    transitionStyle="fade"
    @handleClose="toggleAssociationsModal"
  >
    <template #title>{{ t('industryAssociations.modalTitle') }}</template>
    <div :class="$style.associationsList" data-testid="associations-list">
      <div
        v-for="(association, index) in associationsList"
        :key="association.associationId"
        :class="$style.associationItem"
        :data-testid="`association-item-${index}`"
      >
        <div :class="$style.iconContainer">
          <img
            :src="getAssociationLogoUrl(association.associationId)"
            :class="$style.customIcon"
            alt="Industry Association Badge"
          />
        </div>
        <div :class="$style.associationHeader">
          <h4 :class="$style.associationName">{{ association.name }}</h4>
        </div>
      </div>
    </div>
  </HgModal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

import { HgModal, HgTooltip } from '@smg-real-estate/domus';
import { useI18n } from 'vue-i18n';
import { isUspiAssociation } from './helpers';
import type { AssociationBadges } from './types';

const { t } = useI18n();

defineOptions({
  name: 'IndustryAssociationBadges',
});

interface Props {
  badges: AssociationBadges;
}
const props = defineProps<Props>();

const isAssociationsModalOpen = ref(false);

const mainBadge = computed(() => {
  return (
    props.badges.associations.find(
      (association) => association.associationId === props.badges.favourite,
    ) || props.badges.associations[0]
  );
});
const shouldRenderBadges = computed(() => {
  return mainBadge.value !== null;
});
const associationsList = computed(() => {
  if (!mainBadge.value || !props.badges.associations) {
    return [];
  }
  const rest = props.badges.associations.filter(
    (association) => association.associationId !== props.badges.favourite,
  );
  return [mainBadge.value, ...rest];
});
const displaySecondBadge = computed(() => {
  return props.badges.associations.length > 1;
});
function toggleAssociationsModal() {
  isAssociationsModalOpen.value = !isAssociationsModalOpen.value;
}
function getAssociationLogoUrl(associationId: string) {
  const MEDIA_URL = import.meta.env.VUE_MEDIA_URL;
  if (isUspiAssociation(associationId)) {
    return `${MEDIA_URL}/logos/IndustryAssociationLogos/uspi-suisse.png`;
  } else {
    return `${MEDIA_URL}/logos/IndustryAssociationLogos/${associationId}.png`;
  }
}
</script>

<style module lang="scss">
@import '@/assets/variables';

.badge {
  display: inline-flex;
  align-items: center;
  gap: $space-50;
  padding: $space-50 $space-50;
  border-radius: 4px;
  border: 1px solid #c8c8c8;
  font-size: 0.875rem;
}

.secondaryBadge {
  @extend .badge;
  cursor: pointer;
}

.badgeContainer {
  margin: $space-50;
  margin-left: 0;
  display: flex;
  align-items: center;
  gap: $space-50;
}

.badgeTooltip {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  font-size: 1rem;
}

.associationItem {
  display: flex;
  flex-direction: row;
  gap: $space-75;
  align-items: center;
  justify-content: flex-start;
  padding: $space-100;
  border-bottom: 1px solid $color-grey-50;

  &:last-child {
    border-bottom: none;
  }
}

.associationHeader {
  display: flex;
  align-items: center;
  gap: $space-50;
}

.iconContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
}

.iconContainerSmall {
  @extend .iconContainer;
  width: 1.5rem;
  height: 1.5rem;
}

.customIcon {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
